import Link from 'next/link';
import styled from 'styled-components';
import _capitalize from 'lodash/capitalize';

import { Loader } from 'shared/components/Loader';
import { variableFontRegular } from 'styles/theme';
import { IFeaturedInCollection } from 'productTemplate/types/FeaturedInCollection';
import { ICustomFeaturedInLink } from 'productTemplate/types/CustomFeaturedInLink';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';

export interface IWindowItemFeaturedInProps {
  customLinks?: ICustomFeaturedInLink[];
  featuredInCollections: IFeaturedInCollection[];
  isLoading?: boolean;
  title: string;
}

const handleClick = (name: string, location: string) => {
  sendTrackingEvent('PDP_RELATED_TAP', {
    destination_name: name,
    destination_location: location,
  });
};

export const WindowItemFeaturedIn = ({
  customLinks,
  featuredInCollections,
  isLoading,
  title,
}: IWindowItemFeaturedInProps) => {
  const isPdpCustomLinksEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PDP_CUSTOM_FEATURED_IN_LINKS,
  );

  if (isLoading) {
    return (
      <WindowItemFeaturedIn.Container>
        <Loader />
      </WindowItemFeaturedIn.Container>
    );
  }

  if (
    isPdpCustomLinksEnabled &&
    featuredInCollections?.length === 0 &&
    (!customLinks || customLinks?.length === 0)
  ) {
    return null;
  }

  if (!isPdpCustomLinksEnabled && featuredInCollections?.length === 0) {
    return null;
  }

  const links = customLinks?.map((link) => link.clickthroughDestination);

  const uniqueFeaturedInCollections = isPdpCustomLinksEnabled
    ? featuredInCollections?.filter(
        (collection) => !links?.includes(`collections/${collection.slug}`),
      )
    : featuredInCollections;

  return (
    <WindowItemFeaturedIn.Wrapper>
      <WindowItemFeaturedIn.Title data-qa={`${title}_condition`.toLowerCase().replace(/ /g, '_')}>
        {title}
      </WindowItemFeaturedIn.Title>
      <WindowItemFeaturedIn.Links data-qa="featured_in_links">
        {isPdpCustomLinksEnabled &&
          customLinks?.map(({ name, clickthroughDestination, slug }) => (
            <Link
              key={slug}
              href={`/${clickthroughDestination}`}
              data-qa="featured_in_custom_link"
              prefetch={false}
              passHref
            >
              <a
                role="link"
                tabIndex={0}
                onClick={() => handleClick(name, `/${clickthroughDestination}`)}
                onKeyDown={() => handleClick(name, `/${clickthroughDestination}`)}
              >
                {_capitalize(name)}
              </a>
            </Link>
          ))}
        {uniqueFeaturedInCollections?.map(({ slug, title: collectionTitle, recordId }) => (
          <Link
            href={`/collections/${slug}`}
            data-qa="collection_link"
            prefetch={false}
            key={recordId || slug}
            passHref
          >
            <a
              role="link"
              tabIndex={0}
              onClick={() => handleClick(collectionTitle, `/collections/${slug}`)}
              onKeyDown={() => handleClick(collectionTitle, `/collections/${slug}`)}
            >
              {_capitalize(collectionTitle)}
            </a>
          </Link>
        ))}
      </WindowItemFeaturedIn.Links>
    </WindowItemFeaturedIn.Wrapper>
  );
};

WindowItemFeaturedIn.Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 68px;
  padding: 16px;
`;

WindowItemFeaturedIn.Wrapper = styled.div`
  align-items: flex-start;
  border-bottom: ${({ theme }) => theme.borders.black};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 16px;
  text-transform: capitalize;
  cursor: pointer;

  html[lang='fr-fr'] & {
    text-transform: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    padding: 16px 16px 40px 16px;
  }
`;

WindowItemFeaturedIn.Title = styled.span`
  width: 50%;

  ${variableFontRegular({ height: '14px', size: '9.5px' })}
`;

WindowItemFeaturedIn.Links = styled.div`
  text-align: right;
  > a {
    display: block;
  }
  > a:not(:last-child) {
    margin-bottom: 8px;
  }
  a:hover {
    text-decoration: underline;
  }
`;
